/*
 * SPDX-FileCopyrightText: 2023 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { getCommentReplies, getCommentsContent } from '../../../../api/comments'
// import { useApplicationState } from '../../../../hooks/common/use-application-state'
import { updateComment, updateCommentBox, updateCommentButton, updateCommentButtonPosition, updateCommentList, updateCommentReplies, updateTextSelection } from '../../../../redux/comments/methods'
import { cypressId } from '../../../../utils/cypress-attribute'
// import { saveSelection } from '../../../../utils/selection-utils'
import CommentBox from '../../../comments/CommentBox'
// import CommentList from '../../../comments/CommentList'
import HighlightButtonsGroup from '../../../comments/HighlightButtonsGroup'
import type { ScrollProps } from '../../../editor-page/synced-scroll/scroll-props'
// import type { LineMarkers } from '../../../markdown-renderer/extensions/linemarker/add-line-marker-markdown-it-plugin'
// import { LinemarkerMarkdownExtension } from '../../../markdown-renderer/extensions/linemarker/linemarker-markdown-extension'
// import { useCalculateLineMarkerPosition } from '../../../markdown-renderer/hooks/use-calculate-line-marker-positions'
// import { useMarkdownExtensions } from '../../../markdown-renderer/hooks/use-markdown-extensions'
// import { MarkdownToReact } from '../../../markdown-renderer/markdown-to-react/markdown-to-react'
// import { useDocumentSyncScrolling } from '../../hooks/sync-scroll/use-document-sync-scrolling'
// import { RendererType } from '../../window-post-message-communicator/rendering-message'
import type { HeightChangeRendererProps } from '../common-markdown-renderer-props'
import ReactMarkdown from './ReactMarkdown'
// import { DocumentTocSidebar } from './document-toc-sidebar'
import styles from './markdown-document.module.scss'
// import useResizeObserver from '@react-hook/resize-observer'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
// import metadataParser from 'markdown-yaml-metadata-parser';
import { useSingleStringUrlParameter } from '@hooks/common/use-single-string-url-parameter'
import { useApplicationState } from '@hooks/common/use-application-state'
import { useCodemirrorReferenceContext } from '@components/editor-page/change-content-context/codemirror-reference-context'
import { ScrollSource } from '@components/editor-page/editor-page-content'
import { setScrollSource } from '@redux/editor/methods'
import { Logger } from '@utils/logger'

export type DocumentMarkdownRendererProps = ScrollProps & HeightChangeRendererProps

const logger = new Logger('DocumentMarkdownRenderer')

/**
 * Renders a Markdown document and handles scrolling, yaml metadata and a floating table of contents.
 *
 * @param onMakeScrollSource The callback to call if a change of the scroll source is requested-
 * @param onScroll The callback to call if the renderer is scrolling.
 * @param scrollState The current {@link ScrollState}
 * @param onHeightChange The callback to call if the height of the document changes
 */
export const DocumentMarkdownRenderer: React.FC<DocumentMarkdownRendererProps> = ({
  // onMakeScrollSource,
  // baseUrl,
  // markdownContentLines,
  // onScroll,
  // scrollState,
  // onHeightChange,
  // newLinesAreBreaks,
  // noteIdOrAlias,
  // onCommentPositionChange = () => {}
}) => {
  const noteIdOrAlias = useSingleStringUrlParameter('noteId', undefined)
  const draftMode = useApplicationState((state) => state.noteDetails.draftMode)
  const markdownContent = useApplicationState((state) => draftMode && state.noteDraft.content !== null ? state.noteDraft.content : state.noteDetails.markdownContent.plain)
  const scrollSource = useApplicationState((state) => state.editorConfig.scrollSource);
  const [codeMirrorRef] = useCodemirrorReferenceContext()
  // const rendererRef = useRef<HTMLDivElement | null>(null)
  // const [rendererSize, setRendererSize] = useState<DOMRectReadOnly>()
  // useResizeObserver(rendererRef.current, (entry) => {
  //   setRendererSize(entry.contentRect)
  // })
  // useEffect(() => onHeightChange?.((rendererSize?.height ?? 0) + 1), [rendererSize, onHeightChange])

  // const internalDocumentRenderPaneRef = useRef<HTMLDivElement>(null)
  // const [internalDocumentRenderPaneSize, setInternalDocumentRenderPaneSize] = useState<DOMRectReadOnly>()
  // useResizeObserver(internalDocumentRenderPaneRef.current, (entry) =>
  //   setInternalDocumentRenderPaneSize(entry.contentRect)
  // )

  // const contentLineCount = useMemo(() => markdownContentLines.length, [markdownContentLines])
  // const [recalculateLineMarkers, onUserScroll] = useDocumentSyncScrolling(
  //   internalDocumentRenderPaneRef,
  //   rendererRef,
  //   contentLineCount,
  //   scrollState,
  //   onScroll
  // )

  const markdownBodyRef = useRef<HTMLDivElement>(null)
  // const currentLineMarkers = useRef<LineMarkers[]>()

  // const extensions = useMarkdownExtensions(
  //   baseUrl,
  //   RendererType.DOCUMENT,
  //   useMemo(() => [new LinemarkerMarkdownExtension((values) => (currentLineMarkers.current = values))], [])
  // )
  // useCalculateLineMarkerPosition(markdownBodyRef, currentLineMarkers.current, recalculateLineMarkers)

  const bubbleUpSelectedRegion =  async (e) => {
    const parentPos = document.getElementById("markdown-content").getBoundingClientRect();
    const selection = window.getSelection();
    updateComment([]);
    updateCommentButton(true);
    updateCommentBox(true);
    if (selection && selection.toString()) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      let { position } = range.commonAncestorContainer.parentNode.dataset;
      const startOffset = range.startOffset;
      const endOffset = range.endOffset;
      // Get exact anchor position for normal html tags (except code, pre)
      position = Number(position) + startOffset;
      let { startline } = range.commonAncestorContainer.parentNode.parentNode.dataset;
      let selectedTextlength = endOffset - startOffset
      // Case for code block
      if (!position && !startline) {
        // Case for span inside code block
        position = range.commonAncestorContainer.parentElement.parentElement.parentElement.parentElement.dataset.position;
        startline = range.commonAncestorContainer.parentElement.parentElement.parentElement.parentElement.dataset.startline;
        selectedTextlength = Number(range.commonAncestorContainer.parentElement.parentElement.parentElement.parentElement.dataset.size);
        
        // Case for normal text without span inside code block
        if (!position && !startline) {
          position = range.commonAncestorContainer.parentElement.parentElement.parentElement.dataset.position;
          startline = range.commonAncestorContainer.parentElement.parentElement.parentElement.dataset.startline;
          selectedTextlength = Number(range.commonAncestorContainer.parentElement.parentElement.parentElement.dataset.size);
        }
      }
      
      const { left, top, width, height } = rect; 
      updateCommentButtonPosition({ left: left - parentPos.left, top: top - parentPos.top - 10, width });
      updateTextSelection({ anchor: Number(position), length: selectedTextlength, lineNumber: Number(startline) })
      updateCommentButton(false);
    } else {
      try {
        let { commentId } = window.getSelection()?.anchorNode?.parentNode.dataset;
        // For Code Block Comment
        if (!commentId) {
          // Code Block Text within span - Comment Id
          commentId = window.getSelection().anchorNode.parentNode.parentNode.parentNode.parentNode.dataset.commentId;
          if (!commentId) {
            // Code Block Text without span - Comment Id
            commentId = window.getSelection().anchorNode.parentNode.parentNode.parentNode.dataset.commentId;
          }
        }
        if (commentId) {
          const response = await getCommentsContent(commentId);
          updateComment(response.data)
          const replyResponse = await getCommentReplies(commentId);
          updateCommentReplies(replyResponse.data)
          updateCommentBox(false);
        }
        
      } catch (error) {
        console.log('error', error)
      }
    }
  }

  // const loadComments = useCallback(async (event: { target: { dataset: { commentId: any } } }) => {
  //   const { commentId } = event.target.dataset;
  //   console.log('commentId', commentId)
  //   if (commentId) {
  //     const response = await getCommentsContent(commentId);
  //     updateComment(response.data)
  //   } else {
  //     updateComment([])
  //   }
    
  //   // updateCommentList(false);
  // }, []);


  const handleScroll = useCallback(() => {
    if (!markdownBodyRef.current || !codeMirrorRef) {
      logger.warn(
        'handleScroll: markdownBodyRef.current or codeMirrorRef is null'
      );
      return;
    }
    const divs = markdownBodyRef.current.querySelectorAll('.scrollable-line');
    for (const div of divs) {
      // check if the element is in viewport
      const rect = div.getBoundingClientRect();
      let currentElementLineNo =
        div.parentElement?.attributes?.['data-startline']?.value;
      if (
        rect.top >= 0 &&
        rect.bottom <= window.innerHeight &&
        currentElementLineNo
      ) {
        // code for scroll to a line in codemirror
        const line = codeMirrorRef?.state.doc.line(
          parseInt(currentElementLineNo)
        );
        if (line) {
          codeMirrorRef.dispatch({
            selection: {
              anchor: line.from,
              head: line.to
            },
            scrollIntoView: true
          });
        }
        break;
      }
    }
  }, [codeMirrorRef, markdownBodyRef]);

  return (
    <div
      className={`${styles['markdown-document']} vh-100 bg-light`}
      // ref={internalDocumentRenderPaneRef}
      onScroll={() => scrollSource === ScrollSource.RENDERER && handleScroll()}
      data-scroll-element={true}
      onMouseOver={() => scrollSource === ScrollSource.EDITOR ? setScrollSource(ScrollSource.RENDERER) : null}
      // onMouseEnter={onMakeScrollSource}
      // onTouchStart={onMakeScrollSource}
      >
      <div className={styles['markdown-document-side']} />
      <div id="markdown-content" className={styles['markdown-document-content']}>
        <div 
        // ref={rendererRef} 
        className={`position-relative`}>
          <div
            {...cypressId('markdown-body')}
            ref={markdownBodyRef}
            data-word-count-target={true}
            className={`mb-3 markdown-body w-100 d-flex flex-column align-items-center`}
            onMouseUp={(e) => (!draftMode ? bubbleUpSelectedRegion(e) : undefined)}
          >
            <ReactMarkdown slug='slug' markdown={markdownContent} />
            
            {/* <MarkdownToReact
              markdownContentLines={markdownContentLines}
              markdownRenderExtensions={extensions}
              newlinesAreBreaks={newLinesAreBreaks}
              allowHtml={true}
            /> */}
          </div>
          <HighlightButtonsGroup />
          <CommentBox articleSlug={noteIdOrAlias} />
        </div>
      </div>
      {/* <DocumentTocSidebar width={internalDocumentRenderPaneSize?.width ?? 0} baseUrl={baseUrl} /> */}
    </div>
  )
}
